import React, { useEffect, useState } from "react";
import { Star, CheckCircle2, MessageSquareQuote } from "lucide-react";
import dayjs from "dayjs";

const Feedback = ({ selectedConversation, setupDetails }) => {
  const [rating, setRating] = useState("");
  const [comments, setComments] = useState("");
  const [resolved, setResolved] = useState("");

  const ticket = selectedConversation?.ticketId;
  const feedback = selectedConversation?.feedback;

  useEffect(() => {
    if (feedback) {
      if (feedback.rating !== undefined && feedback.rating !== null) {
        setRating(feedback.rating);
      }
      if (feedback.comments) {
        setComments(feedback.comments);
      }
      if (feedback.resolved) {
        setResolved(feedback.resolved);
      }
    }
  }, [feedback]);

  const closedAt = ticket?.updatedAt || ticket?.createdAt;
  const formattedClosedTime = closedAt
    ? dayjs(closedAt).format("MMM DD, YYYY · hh:mm A")
    : null;

  const numericRating = parseFloat(rating) || 0;

  const getRatingLabel = (score) => {
    if (score >= 5) return "Excellent";
    if (score >= 4) return "Very Good";
    if (score >= 3) return "Satisfied";
    if (score >= 2) return "Could be better";
    if (score >= 1) return "Poor";
    return "";
  };

  return (
    <div className="w-full space-y-3 my-2 animate-in fade-in duration-200">
      {/* Closed Timestamp Badge - No overlaps */}
      {formattedClosedTime && (
        <div className="flex items-center justify-center gap-1.5 py-1.5 px-3.5 bg-slate-100/90 text-slate-600 rounded-full text-xs font-medium w-fit mx-auto shadow-2xs">
          <CheckCircle2 className="w-3.5 h-3.5 text-slate-500" />
          <span>This ticket was closed on {formattedClosedTime}</span>
        </div>
      )}

      {/* Feedback Card */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 text-left space-y-3">
        <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200/70 flex items-center justify-center shrink-0">
              <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
            </div>
            <span className="text-xs font-bold text-slate-900">
              Customer Feedback
            </span>
          </div>

          {resolved && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                resolved === "Yes"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200"
              }`}
            >
              {resolved === "Yes" ? "Resolved" : "Not Resolved"}
            </span>
          )}
        </div>

        {/* Rating Display */}
        <div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((starIndex) => (
                <Star
                  key={starIndex}
                  className={`w-4 h-4 ${
                    numericRating >= starIndex
                      ? "text-amber-400 fill-amber-400"
                      : "text-slate-200 fill-slate-100"
                  }`}
                />
              ))}
            </div>

            {numericRating > 0 ? (
              <span className="text-xs font-bold text-slate-800">
                {numericRating}.0{" "}
                <span className="text-slate-400 font-normal">
                  ({getRatingLabel(numericRating)})
                </span>
              </span>
            ) : (
              <span className="text-[11px] text-slate-400 font-medium">
                No star rating provided
              </span>
            )}
          </div>
        </div>

        {/* Comments Box */}
        {comments ? (
          <div className="mt-2 p-3 bg-slate-50/80 rounded-xl border border-slate-100 flex items-start gap-2">
            <MessageSquareQuote className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed italic break-words flex-1 m-0">
              "{comments}"
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 italic m-0 pt-0.5">
            No written comment was provided.
          </p>
        )}
      </div>
    </div>
  );
};

export default Feedback;
